import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import dayjs from 'dayjs';
import './ProjectEffortEDR.scss';

import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Toast } from 'primereact/toast';
import { useAuth } from '../../../Hooks/useAuth';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { EDR_PROJECT_EFFORTS } from '../../../Api/resources/endPoint';
import {
  getExportFileName,
  getExportFileNameWithDate,
} from '../../Delivery/utils';

/* -------------------------------------------------------------------------- */
/*                                   Base URL                                 */
/* -------------------------------------------------------------------------- */

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${EDR_PROJECT_EFFORTS}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${EDR_PROJECT_EFFORTS}`;
};

const baseURL = fetchBaseURL();

/* -------------------------------------------------------------------------- */
/*                                Nav Links                                   */
/* -------------------------------------------------------------------------- */

const EDR_NAV_LINKS = [
  { label: 'EDR Activity Report', path: '/mis/edr-activity-report' },
  { label: 'Project Allocation % Report', path: '/mis/project-allocation' },
];

const applyToolbarButtonClasses = (rootNode) => {
  if (!rootNode) return;
  rootNode.querySelectorAll('.button-group button').forEach((btn) => {
    const isReset = Boolean(btn.querySelector('.pi-refresh'));
    const isFilter = btn.classList.contains('table-utility-filter-btn');
    const isNew = btn.classList.contains('table-utility-add-new-btn');
    const isViewToggle = btn.classList.contains('tsr-view-toggle-btn');
    const isExport = !isFilter && !isReset && !isNew && !isViewToggle && Boolean(btn.querySelector('svg'));
    btn.classList.toggle('time-sheet-mis-reset-btn', isReset);
    btn.classList.toggle('time-sheet-mis-export-btn', isExport);
    btn.classList.toggle('time-sheet-mis-add-btn', isNew);
  });
};

/* -------------------------------------------------------------------------- */
/*                               Toolbar Heading                              */
/* -------------------------------------------------------------------------- */

const ToolBarHeading = () => (
  <div className="edr-toolbar-heading">
    <div className="links">
      {EDR_NAV_LINKS.map(({ label, path }) => (
        <NavLink
          key={path}
          to={path}
          className={({ isActive }) =>
            `link ${isActive ? 'link--active' : ''}`
          }
        >
          {label}
        </NavLink>
      ))}
    </div>
  </div>
);

const formatProjectManagerHeader = (column) => {
  const header = column?.header;

  if (header !== 'Project Manager / Program Manager') {
    return column;
  }

  return {
    ...column,
    header: (
      <span className="project-manager-program-manager-header">
        <span>Project Manager /</span>
        <br />
        <span>Program Manager</span>
      </span>
    ),
    className: 'project-manager-program-manager-header',
  };
};

/* -------------------------------------------------------------------------- */
/*                      Filtered API Response Handler                         */
/* -------------------------------------------------------------------------- */

//  No longer mutates `response` directly, returns a new object
const handleFilteredApiResponse = (response) => {
  if (!response) return response;

  let rawTableConfiguration = [];

  if (Array.isArray(response.data?.tableConfiguration)) {
    rawTableConfiguration = response.data.tableConfiguration;
  } else if (Array.isArray(response.data?.data?.tableConfiguration)) {
    rawTableConfiguration = response.data.data.tableConfiguration;
  }

  const rawTableData
    = response.data?.tableData || response.data?.data?.tableData || [];

  const grandTotals
    = response.data?.grandTotals || response.data?.data?.grandTotals;

  const tableDataWithFooter = grandTotals && rawTableData.length > 0
    ? [
      ...rawTableData,
      {
        projectId: '',
        projectVertical: '',
        projectName: 'Total',
        projectManagers: '',
        offeringName: '',
        projectStatus: '',
        projectType: '',
        loggedEfforts: grandTotals.totalLoggedEfforts ?? 0,
        unloggedEfforts: grandTotals.totalUnloggedEfforts ?? 0,
        totalEfforts: grandTotals.totalEfforts ?? 0,
        loggedEffortsPercent: '',
        weekendEfforts: grandTotals.totalWeekendEfforts ?? 0,
        weekendEffortsPercent: '',
        rowConfiguration: {
          isFrozen: true,
          template: [
            { field: 'projectName', fontWeight: '700' },
            { field: 'loggedEfforts', fontWeight: '700' },
            { field: 'unloggedEfforts', fontWeight: '700' },
            { field: 'totalEfforts', fontWeight: '700' },
            { field: 'weekendEfforts', fontWeight: '700' },
          ],
        },
        _isFooterRow: true,
      },
    ]
    : rawTableData;

  const pagination
    = response.data?.pagination
    || response.data?.paginationConfig
    || response.data?.data?.pagination
    || response.data?.data?.paginationConfig;

  return {
    ...response,
    data: {
      ...response.data,
      tableConfiguration: rawTableConfiguration.map((column) => ({
        ...formatProjectManagerHeader(column),
        menuItems: Array.isArray(column?.menuItems)
          ? column.menuItems.map((menuItem) => ({
            ...menuItem,
            items: Array.isArray(menuItem?.items)
              ? menuItem.items.map((item) => {
                if (item?.type !== 'filter') return item;
                if (item?.field === 'dateRange') {
                  return {
                    ...item,
                    tableUtilityConfig: {
                      ...(item?.tableUtilityConfig || {}),
                      requiredOnMainApply: true,
                      requiredOnMainApplyWhenFieldsActive: [
                        'projectVertical',
                        'projectName',
                        'offeringName',
                        'projectStatus',
                        'projectType',
                      ],
                      requiredValidationMessage:
                        'Please set both start date and end date before applying filters.',
                    },
                  };
                }
                return item;
              })
              : menuItem?.items,
          }))
          : [],
      })),
      tableData: tableDataWithFooter,
      data: {
        ...(response.data?.data || {}),
        tableData: tableDataWithFooter,
      },
    },
    meta: {
      ...(response.meta || {}),
      page: pagination?.page ?? response.meta?.page ?? 1,
      limit: pagination?.limit ?? response.meta?.limit ?? 10,
      total:
        pagination?.total
        ?? pagination?.count
        ?? response.meta?.total
        ?? rawTableData.length
        ?? 0,
    },
  };
};

const useProjectEffortTableEffects = ({
  containerRef,
  resetToCurrentDate,
  setTableScrollHeight,
}) => {
  useEffect(() => {
    const rootNode = containerRef.current;

    const handleResetClick = (event) => {
      if (!rootNode) return;
      const resetButton = event.target.closest('button');
      if (!resetButton || !rootNode.contains(resetButton)) return;
      const clickedResetIcon
        = Boolean(event.target.closest('.pi-refresh'))
        || Boolean(resetButton.querySelector('.pi-refresh'));
      if (clickedResetIcon) resetToCurrentDate();
    };

    rootNode?.addEventListener('click', handleResetClick);

    const handleResize = () => {
      setTableScrollHeight(`${Math.floor(window.innerHeight * 0.7)}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      rootNode?.removeEventListener('click', handleResetClick);
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef, resetToCurrentDate, setTableScrollHeight]);
};

/* -------------------------------------------------------------------------- */
/*                         Project Effort Component                           */
/* -------------------------------------------------------------------------- */

const ProjectEffort = () => {
  const { userProfile } = useAuth();
  const containerRef = useRef(null);
  const toastRef = useRef(null);

  const [tableScrollHeight, setTableScrollHeight] = useState('400px');
  const [toDate, setToDate] = useState(dayjs());
  const domEnhancementRafRef = useRef(null);

  const handleFilteredApiResponseWithState = useCallback((response) => {
    const message = response?.data?.message || response?.data?.data?.message;
    if (response?.data?.success === false && message) {
      toastRef.current?.show({ severity: 'error', summary: 'Validation Error', detail: message, life: 5000 });
    }

    return handleFilteredApiResponse(response);
  }, []);

  const formattedToDate = toDate ? dayjs(toDate).format('DD-MMM-YYYY') : ''; // Formatting to match requested string

  const handleCellClick = useCallback(({ fieldPath, rowData }) => {
    if (fieldPath !== 'projectName') return;

    const projectId = rowData?.projectId;
    if (!projectId) return;

    const projectDetailsUrl = `/delivery/project-details?projectId=${encodeURIComponent(String(projectId))}`;
    window.open(projectDetailsUrl, '_blank', 'noopener,noreferrer');
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let observer = null;
    const scheduleEnhancements = () => {
      if (domEnhancementRafRef.current) return;
      domEnhancementRafRef.current = window.requestAnimationFrame(() => {
        domEnhancementRafRef.current = null;
        if (observer) observer.disconnect();
        applyToolbarButtonClasses(container);
        if (observer) observer.observe(container, { childList: true, subtree: true });
      });
    };

    scheduleEnhancements();
    observer = new MutationObserver(() => scheduleEnhancements());
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      if (domEnhancementRafRef.current) {
        window.cancelAnimationFrame(domEnhancementRafRef.current);
        domEnhancementRafRef.current = null;
      }
      if (observer) observer.disconnect();
    };
  }, []);

  //  Wrapped in useCallback to stop re-rendering loop
  const resetToCurrentDate = useCallback(() => {
    setToDate(dayjs());
  }, []);

  useProjectEffortTableEffects({
    containerRef,
    resetToCurrentDate,
    setTableScrollHeight,
  });

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,

      userInfo: {
        emailId: userProfile.email,
        permissions: userProfile.permissions,
        roleDetails: userProfile.roleDetails,
        employeeId: userProfile.employeeId,
        globalColors: userProfile?.siteConfiguration?.global_colors,
      },

      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: true },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: true },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          // Passing the date range inside the file names
          filename: () => getExportFileName('EDR Project Effort'),
          sheetName: () => getExportFileNameWithDate('EDR Project Effort'),
        },
      },

      handler: {
        onCellClickHandler: handleCellClick,
        handleFilteredApiResponse: handleFilteredApiResponseWithState,
      },

      additionalPayloadData: {
        toDate: formattedToDate,
      },

      headers: {
        token: userProfile.access_token,
      },

      showToolBar: true,
      showPagination: false,
      showTieredMenu: true,

      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: true,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: 'EDR Project Effort',
        additionalElements: <ToolBarHeading />,
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
        showFilterOption: true,
      },
    },

    tableSettings: {
      size: 'small',
      loaderSkeleton: {
        rows: 10,
        bodyCells: 2,
        columns: 5,
      },
      showExpandCollapse: false,
      tableScrollHeight,
      maxBreakdowns: 4,
      maxFrozenColumns: {
        desktop: 2,
        tablet: 2,
        mobile: 1,
      },
    },
  }), [
    baseURL,
    userProfile,
    formattedToDate,
    tableScrollHeight,
    handleFilteredApiResponseWithState,
    handleCellClick,
  ]);

  return (
    <div className="edr-project-effort-outer-layer">
      <Toast ref={toastRef} />
      <div className="edr-project-effort">
        <div className="edr-project-effort-table-utility" ref={containerRef}>
          <TableUtility configuration={configuration} />
        </div>
      </div>
    </div>
  );
};

export default ProjectEffort;
